import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertTriangle,
  Archive,
  ChevronRight,
  Inbox,
  KanbanSquare,
  List,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils/cn'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  groupRows,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  useTableGrouping,
  ViewToggle,
  type GroupAccent,
  type TableGroup,
} from '@/components/ui'
import { useIntakeList, useIntakeStats, useTransitionIntake } from '../api/queries'
import type { Stage, IntakeSummary } from '../api/types'
import {
  BOARD_STAGES,
  STAGE_LABELS,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
  EXTRACTION_STATE_LABELS,
} from '../utils/stage-meta'
import { IntakeLinesPreview } from '../components/IntakeLinesPreview'
import { SalesIntakeUploadDrawer } from '../components/SalesIntakeUploadDrawer'
import { SalesPipelineKanban } from '../components/SalesPipelineKanban'

type ViewMode = 'table' | 'kanban'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
]

const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'stage', label: 'Group by stage' },
  { value: 'customer', label: 'Group by customer' },
]

/** Map the stage chip variant (which has 'info') onto a DataTable GroupAccent. */
function stageAccent(stage: Stage): GroupAccent {
  const v = STAGE_VARIANT[stage]
  if (v === 'success' || v === 'warning' || v === 'danger') return v
  return 'neutral'
}

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function confidenceBadge(
  confidence: string | null,
  extractionState: 'pending' | 'running' | 'complete' | 'failed',
): { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' } {
  if (extractionState === 'pending' || extractionState === 'running') {
    return { label: '…', variant: 'neutral' }
  }
  if (extractionState === 'failed') {
    return { label: '×', variant: 'danger' }
  }
  const c = confidence == null ? null : Number(confidence)
  if (c == null || Number.isNaN(c)) {
    return { label: '?', variant: 'neutral' }
  }
  if (c >= 0.9) return { label: 'High', variant: 'success' }
  if (c >= 0.5) return { label: 'Med', variant: 'warning' }
  return { label: 'Low', variant: 'danger' }
}

export function SalesInboxPage() {
  const search = useSearch({ strict: false }) as { action?: string }
  const navigate = useNavigate()
  const [uploadOpen, setUploadOpen] = useState(search.action === 'upload')
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [view, setView] = useState<ViewMode>('kanban')
  const [showArchived, setShowArchived] = useState(false)
  // Pending-Odoo lane (WS-C / D-B): cards linked to an Odoo SO but not yet
  // confirmed live off the active board. Mutually exclusive with archived.
  const [showPendingOdoo, setShowPendingOdoo] = useState(false)
  // Table-view row expand: reveals product lines inline. Collapsed by
  // default; only orders with >1 line get the toggle (see useIntakeColumns).
  const [expandedIntakeIds, setExpandedIntakeIds] = useState<Set<string>>(new Set())
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIntakeIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), 200)
    return () => clearTimeout(id)
  }, [searchTerm])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const openIntake = (id: string) =>
    navigate({ to: '/sales-inbox/$intakeId', params: { intakeId: id } })

  const stats = useIntakeStats()
  const list = useIntakeList({
    ...(stageFilter === 'all' ? {} : { status: stageFilter }),
    archived: showArchived,
    pending_odoo: showPendingOdoo,
  })
  const transition = useTransitionIntake()

  const items = list.data?.items ?? []
  const filtered = debouncedSearch.trim()
    ? items.filter((row) => {
        const needle = debouncedSearch.trim().toLowerCase()
        return (
          (row.customer_name?.toLowerCase().includes(needle)) ||
          (row.customer_po_number?.toLowerCase().includes(needle)) ||
          (row.odoo_so_name?.toLowerCase().includes(needle))
        )
      })
    : items

  // C&C command-and-control KPIs — server-computed over active rows.
  // New Orders = freshly staged + awaiting first touch; In Process =
  // actively worked; Overdue = past due being confirmed (>2 business days).
  const newOrders = stats.data?.new_orders ?? 0
  const inProcess = stats.data?.in_process ?? 0
  const overdue = stats.data?.overdue ?? 0
  const pendingOdoo = stats.data?.pending_odoo ?? 0
  // Inbound POs CARLA couldn't auto-file (escalated/failed Orders triage
  // rows). Surfaced so silent escalations don't go unseen.
  const escalatedUnfiled = stats.data?.escalated_unfiled ?? 0

  const columns = useIntakeColumns(expandedIntakeIds, toggleExpanded)

  // Group-by-stage for the table view (persisted per user). 'none' → flat.
  const { groupBy, setGroupBy, collapsedGroups, toggleGroup } =
    useTableGrouping('sales-inbox')
  const groups = useMemo<TableGroup<IntakeSummary>[] | undefined>(() => {
    if (view !== 'table') return undefined
    if (groupBy === 'stage') {
      return groupRows(filtered, {
        keyOf: (r) => r.status,
        groupOrder: BOARD_STAGES,
        labelOf: (k) => STAGE_LABELS[k as Stage] ?? k,
        accentOf: (k) => stageAccent(k as Stage),
      })
    }
    if (groupBy === 'customer') {
      return groupRows(filtered, {
        keyOf: (r) => r.customer_name ?? '',
        labelOf: (k) => (k === '__none__' ? '(no customer)' : k),
      })
    }
    return undefined
  }, [view, groupBy, filtered])

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Sales Inbox"
        description="Command & control for new sales orders — what CARLA has staged and what needs your review before it becomes an Odoo order."
      >
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="New orders" value={String(newOrders)} />
            <MetricCard variant="inline" label="In process" value={String(inProcess)} />
            <MetricCard variant="inline" label="Overdue" value={String(overdue)} />
            <MetricCard variant="inline" label="Pending Odoo" value={String(pendingOdoo)} />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      {escalatedUnfiled > 0 ? (
        <button
          type="button"
          onClick={() => navigate({ to: '/maildesk-triage' })}
          role="alert"
          className="flex flex-shrink-0 items-center gap-2 border-b border-warning bg-warning-soft px-6 py-2 text-left text-sm hover:bg-warning-soft/80"
        >
          <AlertTriangle size={16} className="flex-shrink-0 text-warning" />
          <span className="flex-1 text-primary">
            <span className="font-medium">
              {escalatedUnfiled} inbound{' '}
              {escalatedUnfiled === 1 ? 'PO' : 'POs'} couldn&apos;t be
              auto-filed
            </span>{' '}
            <span className="text-secondary">
              — CARLA escalated these (often a missing PO attachment). Review
              in Triage to file or dismiss them.
            </span>
          </span>
          <span className="text-role-data-secondary text-link">Open Triage →</span>
        </button>
      ) : null}

      <Toolbar>
        <SearchInput
          ref={searchInputRef}
          containerClassName="flex-1 max-w-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search PO #, customer, SO #…"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            variant={showPendingOdoo ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setShowPendingOdoo((v) => !v)
              setShowArchived(false)
            }}
            aria-pressed={showPendingOdoo}
            title="Orders linked to an Odoo SO, awaiting Odoo confirmation"
          >
            {showPendingOdoo ? 'Showing pending Odoo' : `Pending Odoo${pendingOdoo ? ` (${pendingOdoo})` : ''}`}
          </Button>
          <Button
            variant={showArchived ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setShowArchived((v) => !v)
              setShowPendingOdoo(false)
            }}
            aria-pressed={showArchived}
          >
            <Archive size={14} className="mr-1.5" />
            {showArchived ? 'Showing archived' : 'Archived'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          {view === 'table' ? (
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              aria-label="Group rows by"
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          ) : null}
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => setUploadOpen(true)}
          >
            <Plus size={14} className="mr-1.5" />
            Upload
          </Button>
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No sales orders here yet"
            description={
              stageFilter === 'all'
                ? 'Drop a customer PO PDF or XLSX into the uploader to get started. CARLA will extract lines and surface what needs your review.'
                : `No intake records match the "${STAGE_FILTER_OPTIONS.find((o) => o.value === stageFilter)?.label ?? stageFilter}" filter.`
            }
            action={
              stageFilter === 'all' ? (
                <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
                  <Plus size={14} className="mr-1.5" />
                  Upload sales orders
                </Button>
              ) : null
            }
          />
        ) : view === 'kanban' && !showArchived && !showPendingOdoo ? (
          <SalesPipelineKanban
            items={filtered}
            onSelect={(intake) => openIntake(intake.id)}
            onTransition={(intake, target) => {
              transition.mutate(
                { intakeId: intake.id, targetStage: target },
                {
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'transition failed'
                    toast.error(msg, { duration: 5000 })
                  },
                },
              )
            }}
          />
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            groups={groups}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
            loading={list.isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => openIntake(r.id)}
            criticalRowPredicate={(r) => r.is_overdue}
            expandedRowIds={expandedIntakeIds}
            renderExpandedRow={(intake) => <IntakeLinesPreview intakeId={intake.id} />}
            moduleId="sales-inbox"
            maxHeight="100%"
            columnWidths={{
              customer: 240,
              customer_po_number: 130,
              line_count: 70,
              total_amount: 110,
              stage: 130,
              extraction: 100,
              order_date: 110,
              created_at: 110,
              odoo_so: 110,
            }}
            minTableWidth={1200}
            emptyState={
              <EmptyState
                variant="no-match"
                title="No matches"
                description="Try clearing the stage filter or your search."
              />
            }
          />
        )}
      </div>

      <SalesIntakeUploadDrawer
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
    </div>
  )
}

// TanStack column defs for the Table view. Accessors expose the raw,
// sortable value (ISO dates sort lexically = chronologically; amounts and
// counts sort numerically); `cell` renders the same chrome the hand-rolled
// rows used. Column ids align to the `columnWidths`/persistence keys.
function useIntakeColumns(
  expandedRowIds: Set<string>,
  onToggleExpand: (id: string) => void,
): ColumnDef<IntakeSummary, unknown>[] {
  return useMemo<ColumnDef<IntakeSummary, unknown>[]>(() => {
    return [
      {
        id: 'customer',
        header: 'Customer',
        accessorFn: (r) => r.customer_name ?? '',
        cell: (ctx) =>
          ctx.row.original.customer_name ?? (
            <em className="text-tertiary">unknown</em>
          ),
      },
      {
        id: 'customer_po_number',
        header: 'Customer PO #',
        accessorFn: (r) => r.customer_po_number ?? '',
        cell: (ctx) => (
          <span className="text-secondary">
            {ctx.row.original.customer_po_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'line_count',
        header: 'Lines',
        accessorKey: 'line_count',
        meta: { numeric: true },
        cell: (ctx) => {
          const { id, line_count, customer_po_number } = ctx.row.original
          if (line_count <= 1) {
            return <span className="text-secondary">{line_count}</span>
          }
          const isExpanded = expandedRowIds.has(id)
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(id)
              }}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} lines for order ${
                customer_po_number ?? id
              }`}
              className="inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-secondary hover:text-primary focus:outline-none focus:shadow-focus"
            >
              <span className="tabular-nums">{line_count}</span>
              <ChevronRight
                size={12}
                className={cn(
                  'shrink-0 text-tertiary transition-transform duration-fast',
                  isExpanded && 'rotate-90',
                )}
                aria-hidden="true"
              />
            </button>
          )
        },
      },
      {
        id: 'total_amount',
        header: 'Total',
        accessorFn: (r) => (r.total_amount ? Number(r.total_amount) : 0),
        meta: { numeric: true },
        cell: (ctx) => {
          const { total_amount } = ctx.row.original
          return (
            <span className="text-secondary">
              {total_amount
                ? `$${Number(total_amount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : '—'}
            </span>
          )
        },
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => STAGE_SHORT_LABELS[r.status],
        cell: (ctx) => (
          <Chip size="xs" variant={STAGE_VARIANT[ctx.row.original.status]} dot>
            {STAGE_SHORT_LABELS[ctx.row.original.status]}
          </Chip>
        ),
      },
      {
        id: 'acknowledged',
        header: 'Ack',
        enableSorting: false,
        size: 72,
        accessorFn: (r) => r.acknowledged_at ?? '',
        cell: (ctx) =>
          ctx.row.original.acknowledged_at ? (
            <Chip
              size="xs"
              variant="info"
              title={`Acknowledged by ${ctx.row.original.acknowledged_by ?? 'unknown'}`}
            >
              Ack'd
            </Chip>
          ) : (
            <span className="text-secondary">—</span>
          ),
      },
      {
        id: 'extraction',
        header: 'Extraction',
        // Sort by confidence; in-flight/failed rows sort below scored ones.
        accessorFn: (r) =>
          r.extraction_confidence == null ? -1 : Number(r.extraction_confidence),
        cell: (ctx) => {
          const row = ctx.row.original
          const confidence = confidenceBadge(
            row.extraction_confidence,
            row.extraction_state,
          )
          const label =
            row.extraction_state === 'complete'
              ? confidence.label
              : EXTRACTION_STATE_LABELS[row.extraction_state]
          return (
            <Chip size="xs" variant={confidence.variant}>
              {label}
            </Chip>
          )
        },
      },
      {
        id: 'order_date',
        header: 'Order date',
        accessorFn: (r) => r.order_date ?? '',
        cell: (ctx) => (
          <span className="text-tertiary">
            {formatDate(ctx.row.original.order_date)}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Uploaded',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-tertiary">
            {formatDate(ctx.row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'odoo_so',
        header: 'Odoo SO',
        accessorFn: (r) => r.odoo_so_name ?? '',
        cell: (ctx) => {
          const { odoo_so_name, odoo_so_url } = ctx.row.original
          if (odoo_so_name && odoo_so_url) {
            return (
              <a
                href={odoo_so_url}
                target="_blank"
                rel="noreferrer"
                className="text-role-data-secondary text-info hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {odoo_so_name}
              </a>
            )
          }
          if (odoo_so_name) {
            return <span className="text-role-data-secondary">{odoo_so_name}</span>
          }
          return <span className="text-secondary">—</span>
        },
      },
    ]
  }, [expandedRowIds, onToggleExpand])
}
